// Stage helpers shared by the spool card, pipeline strip, and stage
// details table. Ported from the production-schedule prototype
// (project/tools/production-schedule.html) so the React app behaves
// the same as the design source.
import {
  HOURS_PER_DAY,
  STAGES,
  STAGE_BY_KEY,
  type SpoolStageDto,
  type StageDefDto,
  type StageKey,
  type StageStatus,
} from "../../../../../../shared/types/production-schedule";
import { addDays } from "./dates";

export const STAGE_STATUS_COLOR: Record<StageStatus, string> = {
  pending: "#9CA09F",
  "in-progress": "#C77700",
  complete: "#2E7D32",
  skipped: "#C8CACA",
};

// Server lazy-seeds spool_stage rows on first edit — a brand-new
// spool has stages: []. denseStages overlays the canonical 17-stage
// workflow on top of whatever rows exist so the UI can render and
// edit every stage from the moment the spool is created. The
// non-chosen welding bays default to skipped so the visible 10-pill
// strip only shows one weld pill.
//
// Synthetic rows carry id: -1 — patchSpoolStage upserts by
// (spoolId, stageKey) on the server, so the first edit to a
// synthetic row promotes it to a real DB row.
// Pick the weld bay key the dense overlay should treat as active for
// a spool. Two-tier preference among non-skipped weld rows:
//   1. A row with real progress (hours > 0, or any date set, or
//      status moved past 'pending'). The workshop has touched it.
//   2. Otherwise the first non-skipped row — a freshly un-skipped
//      bay from a BayPicker swap (skip old + un-skip new) qualifies
//      so the swap sticks across the next refetch.
// Falls back to the parent job's bay assignment ONLY for a
// genuinely fresh spool with no persisted weld rows at all. When
// weld rows exist but are all skipped, the user explicitly cleared
// welding via the BayPicker's "—" button, so we return null and
// respect that — otherwise the job-bay fallback would immediately
// re-select a bay the user just cleared. Returns null when nothing's
// known — callers should default to weldbay1 in that case.
//
// The two-tier preference matches useWeldBayBySpool in
// JobModalBody so the same chosen bay is computed everywhere — the
// Kanban "spools complete" count, the KPI tiles, the spool draft
// completion check, and the SpoolCard's pill strip all agree.
export function inferWeldBayKey(
  stages: SpoolStageDto[],
  jobBay: number | null,
): StageKey | null {
  const weldRows = stages.filter((st) => st.stageKey.startsWith("weldbay"));
  const candidates = weldRows.filter((st) => st.status !== "skipped");
  const worked = candidates.find(
    (st) =>
      st.hours > 0 ||
      st.startDate !== null ||
      st.doneDate !== null ||
      st.status !== "pending",
  );
  const chosen = worked ?? candidates[0];
  if (chosen) return chosen.stageKey as StageKey;
  // No active weld bay. Only fall back to the job's bay for a spool
  // with no persisted weld rows at all — an all-skipped spool means
  // the user deliberately turned welding off.
  if (weldRows.length === 0 && jobBay && jobBay >= 1 && jobBay <= 8) {
    return `weldbay${jobBay}` as StageKey;
  }
  return null;
}

// Numeric bay (1..8) chosen for a single spool, derived the same way
// as inferWeldBayKey. Returns null when no weld row is non-skipped.
export function spoolChosenBay(stages: SpoolStageDto[]): number | null {
  const chosen = stages
    .filter((st) => st.stageKey.startsWith("weldbay"))
    .find((st) => st.status !== "skipped");
  if (!chosen) return null;
  const n = Number(chosen.stageKey.slice("weldbay".length));
  return Number.isFinite(n) && n >= 1 && n <= 8 ? n : null;
}

// Set of bays a job's work touches (per-spool union). Falls back to
// the legacy job.bay column when nothing's been picked at the spool
// level yet so transitional jobs don't vanish from BaysView etc.
export function jobBaysFor(args: {
  spools: { stages: SpoolStageDto[] }[];
  jobBay: number | null;
}): number[] {
  const out = new Set<number>();
  for (const sp of args.spools) {
    const b = spoolChosenBay(sp.stages);
    if (b != null) out.add(b);
  }
  if (out.size === 0 && args.jobBay != null) out.add(args.jobBay);
  return Array.from(out).sort((a, b) => a - b);
}

export function denseStages(
  stages: SpoolStageDto[],
  weldBayKey: StageKey | null,
  defaultAssigneeByStage?: Map<string, number | null>,
  catalogue?: StageDefDto[],
): SpoolStageDto[] {
  const byKey = new Map<string, SpoolStageDto>();
  for (const s of stages) byKey.set(s.stageKey, s);
  const chosenBay = weldBayKey ?? "weldbay1";
  // Prefer the runtime catalogue (lets archived stages drop out
  // and custom stages appear); fall back to the canonical const so
  // tests and SSR-style renders without the provider still work.
  const defs = (catalogue ?? STAGES.map((s, i) => ({
    stageKey: s.key,
    short: s.short,
    name: s.name,
    color: s.color,
    hasHours: s.hasHours,
    weld: Boolean(s.weld),
    bay: s.bay ?? null,
    sortOrder: i,
    archived: false,
    isCustom: false,
    updatedAt: "",
  } as StageDefDto))).filter((d) => !d.archived);
  return defs.map((def): SpoolStageDto => {
    const existing = byKey.get(def.stageKey);
    if (existing) return existing;
    const isWeldBay = def.weld === true;
    const defaultStatus: StageStatus = isWeldBay && def.stageKey !== chosenBay
      ? "skipped"
      : "pending";
    return {
      id: -1,
      spoolId: stages[0]?.spoolId ?? -1,
      stageKey: def.stageKey as StageKey,
      status: defaultStatus,
      hours: 0,
      assigneeId: defaultAssigneeByStage?.get(def.stageKey) ?? null,
      startDate: null,
      doneDate: null,
    };
  });
}

// Calendar footprint of a stage: ceil(hours / 8), minimum one day.
// Dispatched has hasHours=false and contributes a single day so the
// cascade still lands on a tangible end date. The catalogue is
// passed in so custom stages picked up at runtime resolve here too;
// callers without a catalogue handy fall through to the canonical
// STAGE_BY_KEY constant.
export function stageDays(
  stage: SpoolStageDto,
  catalogue?: StageDefDto[] | Map<string, StageDefDto>,
): number {
  const def = lookupDef(stage.stageKey, catalogue);
  if (!def || !def.hasHours) return 1;
  return Math.max(1, Math.ceil((stage.hours || 0) / HOURS_PER_DAY) || 1);
}

function lookupDef(
  stageKey: string,
  catalogue?: StageDefDto[] | Map<string, StageDefDto>,
): { hasHours: boolean; weld?: boolean } | undefined {
  if (catalogue) {
    if (catalogue instanceof Map) return catalogue.get(stageKey);
    return catalogue.find((s) => s.stageKey === stageKey);
  }
  return STAGE_BY_KEY[stageKey];
}

// Computed finish date for the row. Uses doneDate when the stage is
// closed out, otherwise startDate + stageDays so a pending stage
// still shows a plausible finish in the table.
export function stageFinish(stage: SpoolStageDto): string | null {
  if (stage.doneDate) return stage.doneDate;
  if (!stage.startDate) return null;
  return addDays(stage.startDate, stageDays(stage));
}

export function activeStages(stages: SpoolStageDto[]): SpoolStageDto[] {
  return stages.filter((s) => s.status !== "skipped");
}

// Stages eligible for the spool's progression: dropped 'skipped'
// rows + every welding bay except the one chosen for this spool.
// activeStages already filters by status, but it doesn't know about
// the per-spool weld bay choice — a legacy/non-chosen weld bay
// that's persisted as 'pending' (or anything other than 'skipped')
// would otherwise leak into the Progress button + pill-click flow.
// This is the canonical list the SpoolCard click handlers walk.
export function progressionStages(
  stages: SpoolStageDto[],
  weldBayKey: StageKey | null,
): SpoolStageDto[] {
  const bay = weldBayKey ?? "weldbay1";
  return stages.filter((s) => {
    if (s.status === "skipped") return false;
    if (s.stageKey.startsWith("weldbay") && s.stageKey !== bay) return false;
    return true;
  });
}

// One-step progression plan for a spool (or job) pipeline. `active`
// is the progression list (skipped + non-chosen weld bays already
// filtered). Returns the patches that advance the pipeline exactly
// one step from wherever it currently is:
//   * an in-progress stage → complete it; the next non-complete
//     stage (if any) goes in-progress;
//   * no in-progress but a pending stage → the first pending goes
//     in-progress;
//   * everything complete (or nothing active) → no patches.
// startDate is seeded to `todayIso` when a stage starts or when a
// stage completes without ever recording a start, so the details
// table never shows a finish without a start. Drives both the
// per-card Progress button semantics and the bulk "progress N
// spools" action.
export interface ProgressStepPatch {
  stageKey: StageKey;
  patch: {
    status: StageStatus;
    startDate?: string | null;
    doneDate?: string | null;
  };
}

export function planProgressStep(
  active: SpoolStageDto[],
  todayIso: string,
): ProgressStepPatch[] {
  if (active.length === 0) return [];
  const cur = active.find((s) => s.status === "in-progress");
  if (cur) {
    const out: ProgressStepPatch[] = [{
      stageKey: cur.stageKey as StageKey,
      patch: {
        status: "complete",
        startDate: cur.startDate ?? todayIso,
        doneDate: cur.doneDate ?? todayIso,
      },
    }];
    const idx = active.indexOf(cur);
    const next = active.slice(idx + 1).find((s) => s.status !== "complete");
    if (next) {
      out.push({
        stageKey: next.stageKey as StageKey,
        patch: { status: "in-progress", startDate: next.startDate ?? todayIso },
      });
    }
    return out;
  }
  const firstPending = active.find((s) => s.status === "pending");
  if (firstPending) {
    return [{
      stageKey: firstPending.stageKey as StageKey,
      patch: { status: "in-progress", startDate: firstPending.startDate ?? todayIso },
    }];
  }
  return [];
}

// Welding-bay swap plan. Selecting a different bay from the W1..W8
// buttons must NOT change the spool's progress — it only relabels
// which bay the (single) weld stage runs in. So the active bay's
// tracking — status, start date, est hrs, finish (doneDate), and
// the assigned welder — migrates onto the newly-chosen bay, and the
// old bay is hidden (skipped) with its tracking cleared so the data
// lives in exactly one place.
//
// `current` is the row of the bay being migrated FROM (the active
// one). Captured by the caller before any patch so the plan uses
// pre-swap values regardless of the order patches land in.
//
// Returns the ordered patch list the caller applies via
// onStagePatch. Empty when from === to (clicking the active bay).
export interface BaySwapPatch {
  stageKey: StageKey;
  patch: {
    status: StageStatus;
    startDate: string | null;
    doneDate: string | null;
    hours: number;
    assigneeId: number | null;
  };
}

export function planBaySwap(args: {
  fromKey: StageKey | null;
  toKey: StageKey | null;
  current: SpoolStageDto | null;
}): BaySwapPatch[] {
  const { fromKey, toKey, current } = args;
  if (fromKey === toKey) return [];
  const out: BaySwapPatch[] = [];
  if (toKey) {
    // The active bay's status carries over so progress is unchanged.
    // Guard against a skipped source (shouldn't happen for the
    // active bay) by falling back to pending.
    const status: StageStatus =
      current && current.status !== "skipped" ? current.status : "pending";
    out.push({
      stageKey: toKey,
      patch: {
        status,
        startDate: current?.startDate ?? null,
        doneDate: current?.doneDate ?? null,
        hours: current?.hours ?? 0,
        // The assigned welder follows the weld work, otherwise the
        // assignment would appear to vanish (it'd stay stranded on
        // the now-skipped old bay while the new bay shows its
        // prior/default assignee).
        assigneeId: current?.assigneeId ?? null,
      },
    });
  }
  if (fromKey && fromKey !== toKey) {
    out.push({
      stageKey: fromKey,
      patch: { status: "skipped", startDate: null, doneDate: null, hours: 0, assigneeId: null },
    });
  }
  return out;
}

// Stage to advance when the user clicks PROGRESS: the in-progress
// one, else the first pending. Returns null when nothing's left
// to move forward.
export function currentStage(stages: SpoolStageDto[]): SpoolStageDto | null {
  const active = activeStages(stages);
  return (
    active.find((s) => s.status === "in-progress") ??
    active.find((s) => s.status === "pending") ??
    null
  );
}

export function nextPending(
  stages: SpoolStageDto[],
  after: SpoolStageDto,
): SpoolStageDto | null {
  const active = activeStages(stages);
  const idx = active.indexOf(after);
  if (idx < 0) return null;
  return active.slice(idx + 1).find((s) => s.status === "pending") ?? null;
}

// Job-level progress for the JobModal top progress bar. Counts the
// progression stages on every spool — the same set the SpoolCard's
// pill strip walks — so an untouched spool with zero persisted
// stage rows still contributes its full active count to the
// denominator. Without this, a job where 3 of 42 spools are done
// would show 100% (the only persisted stages happen to all be
// complete) instead of ~7%.
export function jobProgress(args: {
  spools: { id: number; stages: SpoolStageDto[] }[];
  catalogue: StageDefDto[];
  weldBayBySpool: Map<number, StageKey | null>;
}): { pct: number; complete: number; total: number } {
  let complete = 0;
  let total = 0;
  for (const sp of args.spools) {
    const weldBayKey = args.weldBayBySpool.get(sp.id) ?? null;
    const dense = denseStages(sp.stages, weldBayKey, undefined, args.catalogue);
    const progression = progressionStages(dense, weldBayKey);
    total += progression.length;
    for (const st of progression) {
      if (st.status === "complete") complete += 1;
    }
  }
  const pct = total === 0 ? 0 : Math.round((complete / total) * 100);
  return { pct, complete, total };
}

// ── Simple View (job-level pipeline) ─────────────────────────────
// Active job stages: not skipped (skipped doubles as "hidden").
export function jobActiveStages(jobStages: SpoolStageDto[]): SpoolStageDto[] {
  return jobStages.filter((s) => s.status !== "skipped");
}

// Progress for a simple-mode job: complete / active across the
// job-level pipeline. Mirrors jobProgress' shape so the modal
// progress bar + Board card can swap in cleanly.
export function jobStageProgress(jobStages: SpoolStageDto[]): {
  pct: number;
  complete: number;
  total: number;
} {
  const active = jobActiveStages(jobStages);
  const complete = active.filter((s) => s.status === "complete").length;
  const total = active.length;
  return { pct: total ? Math.round((complete / total) * 100) : 0, complete, total };
}

export function jobStageTotalHours(jobStages: SpoolStageDto[]): number {
  return jobActiveStages(jobStages).reduce((a, s) => a + (s.hours || 0), 0);
}

// The weld bay the derived job pipeline should run in: the most
// common chosen bay across the spools, else the job's bay, else
// weldbay1.
function deriveJobChosenBay(
  spools: { stages: SpoolStageDto[] }[],
  jobBay: number | null,
): StageKey {
  const counts = new Map<string, number>();
  for (const sp of spools) {
    const b = inferWeldBayKey(sp.stages, null);
    if (b) counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  let bay: string | null = null;
  let best = 0;
  for (const [k, v] of counts) if (v > best) { best = v; bay = k; }
  if (bay) return bay as StageKey;
  if (jobBay && jobBay >= 1 && jobBay <= 8) return `weldbay${jobBay}` as StageKey;
  return "weldbay1" as StageKey;
}

// Seed a job's stage pipeline from its spools on first flip to
// Simple. Per stage key, roll up across the spools where that stage
// is active: sum hours, earliest start, most-used assignee, status
// (all complete → complete; any in-progress → in-progress; else
// pending). A stage active on no spool is skipped. A job pipeline
// supports MULTIPLE weld bays: every bay that at least one spool
// actually runs in seeds active (with that subset's rollup); for
// weld rows only the spool's chosen bay counts, so a legacy orphan
// 'pending' row on a non-chosen bay doesn't activate a job-level
// bay nobody welds in. A job with no spools → a fresh pipeline on
// the single derived bay (others skipped).
export function deriveJobStages(
  spools: { stages: SpoolStageDto[] }[],
  catalogue: StageDefDto[],
  jobBay: number | null,
): SpoolStageDto[] {
  const chosen = deriveJobChosenBay(spools, jobBay);
  const base = denseStages([], chosen, undefined, catalogue).map((s) => ({
    ...s,
    spoolId: 0,
  }));
  if (spools.length === 0) return base;
  const chosenBySpool = spools.map((sp) => inferWeldBayKey(sp.stages, null));
  return base.map((st): SpoolStageDto => {
    const isWeld = st.stageKey.startsWith("weldbay");
    const rows = spools
      .map((sp, i) => {
        if (isWeld && chosenBySpool[i] !== st.stageKey) return undefined;
        return sp.stages.find((x) => x.stageKey === st.stageKey);
      })
      .filter((x): x is SpoolStageDto => x !== undefined && x.status !== "skipped");
    if (rows.length === 0) return { ...st, status: "skipped" };
    const hours = rows.reduce((a, x) => a + (x.hours || 0), 0);
    const startDate =
      rows.map((x) => x.startDate).filter((d): d is string => Boolean(d)).sort()[0] ?? null;
    const counts = new Map<number, number>();
    for (const x of rows) {
      if (x.assigneeId != null) counts.set(x.assigneeId, (counts.get(x.assigneeId) ?? 0) + 1);
    }
    let assigneeId: number | null = null;
    let best = 0;
    for (const [k, v] of counts) if (v > best) { best = v; assigneeId = k; }
    const status: StageStatus = rows.every((x) => x.status === "complete")
      ? "complete"
      : rows.some((x) => x.status === "in-progress")
        ? "in-progress"
        : "pending";
    return { ...st, status, hours, startDate, assigneeId, doneDate: null };
  });
}

export function spoolLatestFinish(stages: SpoolStageDto[]): string | null {
  let latest: string | null = null;
  for (const s of activeStages(stages)) {
    const end = stageFinish(s);
    if (end && (!latest || end > latest)) latest = end;
  }
  return latest;
}

// True when every progression stage on the spool is complete. Uses
// the same denseStages + progressionStages overlay as SpoolCard so
// the Kanban "N/M spools complete" count agrees with the per-spool
// percentage on the card.
//
// Returns false for spools with no persisted stage rows yet: an
// untouched spool gets a synthetic progression list of all-pending
// rows, so `every === "complete"` is false. That's the workshop
// expectation — a fresh spool isn't "done" just because the
// denormalised spool.status hasn't been touched.
export function isSpoolComplete(args: {
  spool: { stages: SpoolStageDto[] };
  catalogue: StageDefDto[];
  jobBay: number | null;
}): boolean {
  const weldBayKey = inferWeldBayKey(args.spool.stages, args.jobBay);
  const dense = denseStages(args.spool.stages, weldBayKey, undefined, args.catalogue);
  const progression = progressionStages(dense, weldBayKey);
  if (progression.length === 0) return false;
  return progression.every((s) => s.status === "complete");
}

export type RolledStatus = "pending" | "in-progress" | "complete";

export function spoolOverallStatus(stages: SpoolStageDto[]): RolledStatus {
  const active = activeStages(stages);
  if (active.length === 0) return "pending";
  if (active.every((s) => s.status === "complete")) return "complete";
  if (active.some((s) => s.status === "in-progress")) return "in-progress";
  return "pending";
}

// Stage rows shown in the pipeline strip + the collapsed stage
// details table. Walks the runtime catalogue (so reordered / custom
// stages appear in the right slot). showAll=true returns every
// catalogue row that has a stage record — including skipped weld
// bays so the workshop can flip them back on. showAll=false trims
// skipped rows and limits welding to the chosen bay so the strip
// renders one weld pill.
export function visibleStageRows(
  stages: SpoolStageDto[],
  weldBayKey: StageKey | null,
  showAll: boolean,
  catalogue?: StageDefDto[],
): SpoolStageDto[] {
  const byKey = new Map<string, SpoolStageDto>();
  for (const s of stages) byKey.set(s.stageKey, s);
  const defs = (catalogue ?? STAGES.map((s) => ({
    stageKey: s.key,
    weld: Boolean(s.weld),
    archived: false,
  } as Partial<StageDefDto> & { stageKey: string; weld: boolean; archived: boolean })))
    .filter((d) => !d.archived);
  const bay = (weldBayKey ?? "weldbay1") as string;
  const rows: SpoolStageDto[] = [];
  for (const def of defs) {
    const s = byKey.get(def.stageKey);
    if (!s) continue;
    if (!showAll) {
      if (s.status === "skipped") continue;
      if (def.weld && def.stageKey !== bay) continue;
    }
    rows.push(s);
  }
  return rows;
}

// Health classification for the spool card's left-border accent.
// Matches the prototype's is-overdue / is-at-risk / is-on-track /
// is-complete buckets.
export type SpoolHealth = "overdue" | "at-risk" | "on-track" | "complete";

export function spoolHealth(args: {
  dueDate: string | null;
  stages: SpoolStageDto[];
  todayIso: string;
}): SpoolHealth {
  const overall = spoolOverallStatus(args.stages);
  if (overall === "complete") return "complete";
  if (!args.dueDate) return "on-track";
  if (args.dueDate < args.todayIso) return "overdue";
  const due = new Date(args.dueDate).getTime();
  const today = new Date(args.todayIso).getTime();
  const days = Math.round((due - today) / 86_400_000);
  if (days <= 7) return "at-risk";
  return "on-track";
}
